/**
 * Pseudo-3D-Helpers (BATTLE-14): Volumen-Gradients, Rim-Light, Specular,
 * weiche Schatten, Hex-Extrusion, Farb-Utils. Lichtquelle: oben-links.
 */

/** '#rrggbb' aufhellen/abdunkeln um pct (-100..100) */
export function shade(hex: string, pct: number): string {
  const n = parseInt(hex.slice(1), 16)
  const amt = Math.round(2.55 * pct)
  const r = Math.min(255, Math.max(0, (n >> 16) + amt))
  const g = Math.min(255, Math.max(0, ((n >> 8) & 0xff) + amt))
  const b = Math.min(255, Math.max(0, (n & 0xff) + amt))
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

export function lighten(hex: string, pct: number): string {
  return shade(hex, Math.abs(pct))
}

export function darken(hex: string, pct: number): string {
  return shade(hex, -Math.abs(pct))
}

/** '#rrggbb' -> 'rgba(...)' */
export function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${n >> 16}, ${(n >> 8) & 0xff}, ${n & 0xff}, ${alpha})`
}

/**
 * Volumen-Gradient fuer einen Koerper um (x, y) mit Radius r:
 * Licht oben-links, Basiston Mitte, Schatten unten-rechts.
 * Als fillStyle fuer den bereits konstruierten Pfad verwenden.
 */
export function volumeGradient(
  ctx: CanvasRenderingContext2D, x: number, y: number, r: number, base: string,
  lightPct = 18, darkPct = 22,
): CanvasGradient {
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.45, r * 0.1, x, y, r * 1.25)
  g.addColorStop(0, lighten(base, lightPct))
  g.addColorStop(0.55, base)
  g.addColorStop(1, darken(base, darkPct))
  return g
}

/** Rim-Light: heller Konturbogen oben-links auf dem aktuellen Pfad-Umriss */
export function rimLight(
  ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number,
  color = 'rgba(255, 250, 235, 0.5)', width = 2,
): void {
  ctx.save()
  ctx.strokeStyle = color
  ctx.lineWidth = width
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.ellipse(x, y, rx * 0.88, ry * 0.88, 0, Math.PI * 1.05, Math.PI * 1.65)
  ctx.stroke()
  ctx.restore()
}

/** kleiner Glanzpunkt (Specular) */
export function specular(
  ctx: CanvasRenderingContext2D, x: number, y: number, r: number, alpha = 0.55,
): void {
  ctx.save()
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, `rgba(255, 255, 250, ${alpha})`)
  g.addColorStop(1, 'rgba(255, 255, 250, 0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

/** weicher Boden-Schatten (Drop Shadow) unter einer Unit */
export function softShadow(
  ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, alpha = 0.4,
): void {
  ctx.save()
  const g = ctx.createRadialGradient(x, y, 0, x, y, rx)
  g.addColorStop(0, `rgba(0, 0, 0, ${alpha})`)
  g.addColorStop(1, 'rgba(0, 0, 0, 0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

/** Aussen-Glow um einen Punkt (Auren, 3-Stern, Casts) */
export function glow(
  ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, alpha = 0.5,
): void {
  ctx.save()
  const g = ctx.createRadialGradient(x, y, r * 0.4, x, y, r)
  g.addColorStop(0, rgba(color, alpha))
  g.addColorStop(1, rgba(color, 0))
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}
